import type { GeneralInfoInProgress, TemporaryCustomsParts } from '@/features/shipment/types';

const EU27_CODES = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR',
  'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK',
  'SI', 'ES', 'SE',
]);

const PID_CARRIERS = new Set(['FedEx', 'UPS', 'DHL', 'TotalShip']);

export function isEuDestination(
  destinationCountryCode: string | undefined,
): boolean {
  if (!destinationCountryCode) return false;
  const normalized = destinationCountryCode === 'HO' ? 'NL' : destinationCountryCode;
  return EU27_CODES.has(normalized);
}

export function isEuPidShipment(
  destinationCountryCode: string | undefined,
  carrierCode: string | undefined,
): boolean {
  if (!destinationCountryCode || !carrierCode) return false;
  const normalized = destinationCountryCode === 'HO' ? 'NL' : destinationCountryCode;
  return EU27_CODES.has(normalized) && PID_CARRIERS.has(carrierCode);
}

// Sanitize request copies only: switching destinations must not erase local PIDs.
export function prepareInvoiceForDestination(
  invoice: GeneralInfoInProgress,
  destinationCountryCode: string | undefined,
): GeneralInfoInProgress {
  if (!invoice || isEuDestination(destinationCountryCode)) return invoice;

  const clearPids = <T extends TemporaryCustomsParts>(part: T): T => {
    const copy = { ...part };
    const keys: Array<keyof Pick<TemporaryCustomsParts,
      'merchantProductId' | 'manufacturerProductId' | 'standardProductId'>> = [
        'merchantProductId', 'manufacturerProductId', 'standardProductId',
      ];
    keys.forEach((key) => {
      if (Object.prototype.hasOwnProperty.call(copy, key)) copy[key] = '';
    });
    return copy;
  };

  return {
    ...invoice,
    customsParts: invoice.customsParts?.map(clearPids),
    ...(invoice.temporaryCustomsParts
      ? { temporaryCustomsParts: clearPids(invoice.temporaryCustomsParts) }
      : {}),
  };
}
